/**
 * What several models agree on, and how much they don't.
 *
 * The ask was "an aggregated forecast eliminating any erroneous models", and the
 * honest version of that is not deletion. There is no way to know from here which
 * model is wrong; there is only the shape of the disagreement. Quietly dropping
 * the outlier and printing one confident number would hide exactly the
 * information a skipper needs, which is whether the models agree at all. Six
 * models saying F5 and six models spanning F3 to F7 are completely different
 * situations, and a single averaged "F5" makes them look identical.
 *
 * So: the MEDIAN, which ignores an outlier by construction rather than by
 * judgement, always reported with the range it came from and how many models fed
 * it. Models far from the median are FLAGGED, not removed, so they are still
 * there to look at. And the spread gets a plain-English name, because "they all
 * agree" and "they are all over the place" is the read most people actually want.
 *
 * Directions need their own maths. The average of 350° and 010° is 000°, not
 * 180°, and any code that treats degrees as ordinary numbers will confidently
 * tell you the wind is from the south when every model said north. Circular
 * statistics throughout for anything in degrees.
 */

/** How much the models disagree, in words. */
export type AgreementLevel = 'tight' | 'fair' | 'poor';

export interface Consensus {
  /** The median across the models that answered. Null if none did. */
  value: number | null;
  /** Lowest and highest of the answers, so the median is never read alone. */
  low: number | null;
  high: number | null;
  /** How many models contributed. */
  count: number;
  /** The spread, in the variable's own units (degrees for a direction). */
  spread: number;
  agreement: AgreementLevel;
  /** Model ids sitting far enough from the median to be worth a second look. */
  outliers: string[];
}

export interface ModelValue {
  modelId: string;
  value: number | null;
}

const EMPTY: Consensus = {
  value: null,
  low: null,
  high: null,
  count: 0,
  spread: 0,
  agreement: 'poor',
  outliers: [],
};

/** Ordinary median. Even counts take the mean of the middle two. */
export function median(values: number[]): number {
  const v = [...values].sort((a, b) => a - b);
  const n = v.length;
  const mid = Math.floor(n / 2);
  return n % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

/** Signed difference from a to b, degrees, in [-180, 180]. */
export function angleDiff(a: number, b: number): number {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

/**
 * The mean of a set of directions, via unit vectors.
 *
 * Used instead of a median for angles because a circular median is ill-defined
 * for an even count and this is well-behaved for the spreads we care about. It
 * is still resistant enough: the outlier check below runs on the angular distance
 * from this centre, so a single wild model shifts it, is flagged, and can be seen.
 */
export function circularMean(degs: number[]): number {
  let x = 0;
  let y = 0;
  for (const d of degs) {
    const r = (d * Math.PI) / 180;
    x += Math.cos(r);
    y += Math.sin(r);
  }
  if (x === 0 && y === 0) return 0;
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/** The widest angular gap in a set of directions, degrees. */
export function circularSpread(degs: number[], centre: number): number {
  let lo = 0;
  let hi = 0;
  for (const d of degs) {
    const diff = angleDiff(centre, d);
    if (diff < lo) lo = diff;
    if (diff > hi) hi = diff;
  }
  return hi - lo;
}

/**
 * Agree on a scalar (wind speed, gust, rain, pressure, wave height).
 *
 * `tightBelow` and `poorAbove` are the variable's own thresholds, because 4 kn of
 * disagreement in wind speed is a lot and 4 hPa of disagreement in pressure is
 * not. `outlierAt` is how far from the median a model has to sit to be flagged.
 */
export function agreeScalar(
  values: ModelValue[],
  opts: { tightBelow: number; poorAbove: number; outlierAt: number },
): Consensus {
  const good = values.filter((v) => v.value != null && Number.isFinite(v.value)) as {
    modelId: string;
    value: number;
  }[];
  if (!good.length) return { ...EMPTY };

  const nums = good.map((g) => g.value);
  const mid = median(nums);
  const low = Math.min(...nums);
  const high = Math.max(...nums);
  const spread = high - low;
  return {
    value: mid,
    low,
    high,
    count: good.length,
    spread,
    agreement: spread <= opts.tightBelow ? 'tight' : spread >= opts.poorAbove ? 'poor' : 'fair',
    outliers: good.filter((g) => Math.abs(g.value - mid) >= opts.outlierAt).map((g) => g.modelId),
  };
}

/**
 * Agree on a direction. Same idea, all of it angular.
 *
 * `low` and `high` come back as the actual compass bearings at the edges of the
 * spread rather than as offsets, so a card can print "from 190° to 240°" without
 * doing the wrap-around itself and getting it wrong.
 */
export function agreeDirection(
  values: ModelValue[],
  opts: { tightBelow: number; poorAbove: number; outlierAt: number },
): Consensus {
  const good = values.filter((v) => v.value != null && Number.isFinite(v.value)) as {
    modelId: string;
    value: number;
  }[];
  if (!good.length) return { ...EMPTY };

  const degs = good.map((g) => ((g.value % 360) + 360) % 360);
  const centre = circularMean(degs);
  let lo = 0;
  let hi = 0;
  for (const d of degs) {
    const diff = angleDiff(centre, d);
    if (diff < lo) lo = diff;
    if (diff > hi) hi = diff;
  }
  const spread = hi - lo;
  return {
    value: centre,
    low: (centre + lo + 360) % 360,
    high: (centre + hi + 360) % 360,
    count: good.length,
    spread,
    agreement: spread <= opts.tightBelow ? 'tight' : spread >= opts.poorAbove ? 'poor' : 'fair',
    outliers: good
      .filter((g) => Math.abs(angleDiff(centre, g.value)) >= opts.outlierAt)
      .map((g) => g.modelId),
  };
}

/**
 * Sensible thresholds per variable, so every call site does not invent its own.
 *
 * Wind: 5 kn apart is close enough to plan on, 15 kn apart is a different day.
 * Direction: 20° is nothing, 60° is the difference between a beat and a reach.
 * Gusts spread more than the mean wind does, so they get more room before we call
 * it poor. Pressure is the tightest agreement of anything, so a 4 hPa split is
 * already the models telling you something.
 */
export const AGREEMENT_THRESHOLDS = {
  windKn: { tightBelow: 5, poorAbove: 15, outlierAt: 10 },
  gustKn: { tightBelow: 8, poorAbove: 20, outlierAt: 14 },
  dirDeg: { tightBelow: 20, poorAbove: 60, outlierAt: 45 },
  pressureHpa: { tightBelow: 2, poorAbove: 6, outlierAt: 5 },
  rainMm: { tightBelow: 0.5, poorAbove: 3, outlierAt: 2 },
  waveM: { tightBelow: 0.3, poorAbove: 1, outlierAt: 0.7 },
} as const;

/** One line about how much to trust the number next to it. */
export function agreementNote(c: Consensus): string {
  if (!c.count) return 'No model answered for this hour.';
  if (c.count === 1) return 'One model only, so there is nothing to compare it with.';
  if (c.agreement === 'tight') return `All ${c.count} models close together.`;
  if (c.agreement === 'fair') return `${c.count} models, some spread. Worth reading the columns.`;
  return `${c.count} models and they disagree. Treat the middle figure with caution.`;
}
